import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Box, ButtonLink } from '@machobear/design-system'
import { FieldSwitchToggle } from '@/components/form'
import useRole from '@/hooks/use-role'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import config from '@/config'
import { SectionSubHeading, SectionRow } from '../elements'
import SettingsLayout from '../Layout'

const { WEB_URL } = config

const SubGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 100px;
  :not(:last-child) {
    padding-bottom: 50px;
    margin-bottom: 50px;
    border-bottom: 1px solid ${colors.grey};
  }
`

const PanelText = styled.p`
  color: ${colors.regular};
  font-size: 14px;
  letter-spacing: -0.2px;
  line-height: 19px;
  margin: 0 0 30px;
`

const Label = styled.label`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  letter-spacing: -0.2px;
  line-height: 22px;
  margin: 0;
`

const SectionSubtitle = styled.span`
  color: ${colors.regular};
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  line-height: 24px;
`

const ItemGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const ListingAlertsContainer = styled(SectionRow)`
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Notifications = () => {
  const { t } = useTranslation()
  const userRole = useRole()

  return (
    <SettingsLayout
      title={t('b.settings.view.notifications.link', 'Notifications')}
      description={t(
        'b.settings.view.notifications.description',
        'Update your notification preferences here.'
      )}
    >
      <SubGroup>
        <SectionSubHeading>{t('common.chat', 'Chat')}</SectionSubHeading>
        <PanelText>
          {userRole === 'tenant'
            ? t(
                'r.settings.view.activity.text',
                'Receive messages and notifications from landlords whenever an event occurs e.g., when a viewing is delayed or when both parties have signed the lease contract.'
              )
            : t(
                'l.settings.view.activity.text',
                'Receive messages and notifications from renters whenever an event occurs e.g., when a viewing is delayed or when both parties have signed the lease contract.'
              )}
        </PanelText>
        <SectionRow>
          <Label>{t('b.settings.view.email.label', 'Email')}</Label>
          <FieldSwitchToggle name="activity_receive_emails" />
        </SectionRow>
        <SectionRow>
          <Label>{t('b.settings.view.push_notifications.label', 'Push notifications')}</Label>
          <FieldSwitchToggle name="activity_receive_push_notifications" />
        </SectionRow>
        <SectionRow>
          <Label>{t('b.settings.view.text_messages.label', 'Text messages')}</Label>
          <FieldSwitchToggle name="activity_receive_text_messages" />
        </SectionRow>
      </SubGroup>
      <SubGroup>
        <SectionSubHeading>
          {t('b.settings.view.payment_reminder.title', 'Payment Reminder')}
        </SectionSubHeading>
        <PanelText>
          {t(
            'b.settings.view.payment_reminder.text',
            'Receive notifications when a payment is due soon.'
          )}
        </PanelText>
        <SectionRow>
          <Label>{t('b.settings.view.email.label', 'Email')}</Label>
          <FieldSwitchToggle name="setting_reminder_rent_payment" />
        </SectionRow>
      </SubGroup>
      <SubGroup>
        <SectionSubHeading>{t('b.settings.view.marketing.title', 'Marketing')}</SectionSubHeading>
        <PanelText>
          {t(
            'b.settings.view.marketing.text',
            'Receive messages, newsletters, marketing emails and deals from liv.rent. '
          )}
        </PanelText>
        <SectionRow>
          <Label>{t('b.settings.view.email.label', 'Email')}</Label>
          <FieldSwitchToggle name="setting_promo_emails" />
        </SectionRow>
      </SubGroup>
      <SubGroup>
        <SectionSubHeading>
          {t('b.settings.view.saved_alerts.title', 'Saved Alerts')}
        </SectionSubHeading>
        <PanelText>
          {t(
            'b.settings.view.saved.text',
            'Receive messages, newsletters, marketing emails and deals from liv.rent. '
          )}
        </PanelText>
        <ListingAlertsContainer>
          <ItemGroup>
            <SectionSubtitle>
              {t('b.settings.view.listing alert.label', 'Listing Alert')}
            </SectionSubtitle>
            <PanelText>
              {t(
                'b.settings.view.alert.text',
                'Receive automated notifications for listings that match your preferences.'
              )}
            </PanelText>
          </ItemGroup>
          <Box marginLeft={{ mobile: 'none', tablet: 'medium' }} style={{ width: 80 }}>
            <ButtonLink
              id="lakshimi-notifications-saved-alerts-manage-click"
              size="small"
              href={`${WEB_URL}/saved-alerts`}
            >
              {t('b.settings.view.manage.text', 'Manage')}
            </ButtonLink>
          </Box>
        </ListingAlertsContainer>
      </SubGroup>
    </SettingsLayout>
  )
}

export default Notifications
